import { useState } from "react";
import Axios from "../services/AxiosInterceptor";
import { Eye, EyeOff, Mail, Lock, User, Phone, MapPin, UserPlus, LogIn } from 'lucide-react';

const CustomerRegister = ({ setLogin }) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobile_no: "",
    location: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    let err = {};
    if (!formData.username.trim()) err.username = "Username is required.";
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) err.email = "Enter a valid email address.";
    
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(formData.mobile_no)) err.mobile_no = "Mobile must be 10 digits.";
    
    if (!formData.location.trim()) err.location = "Location is required.";
    if (formData.password.length < 6) err.password = "Password must be 6+ characters.";
    if (formData.password !== formData.confirmPassword) err.confirmPassword = "Passwords don't match.";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await Axios.post("api/singup_cx", {
        email: formData.email,
        password: formData.password,
        location: formData.location,
        mobile_no: formData.mobile_no,
        username: formData.username,
      });

      alert("Registration successful! Welcome to Yogametic Family.");
      window.location.reload();
    } catch (error) {
      setLoading(false);
      const result = error.response?.data;
      if (result?.message === "Email already exists!") {
        setErrors({ email: "Email already exists!" });
      } else {
        setErrors({ submit: "Something went wrong." });
      }
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-orange-50 via-white to-blue-50 flex items-center justify-center p-4">
      
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 bg-white rounded-2xl shadow-2xl overflow-hidden">
        
        {/* Left Side - Branding */}
        <div className="hidden lg:flex relative bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 p-8 flex-col justify-center overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          
          <div className="relative z-10 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <UserPlus size={40} className="text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Join Yogamatic</h2>
            <p className="text-orange-100 text-lg mb-6">Start your wellness journey today</p>

            <div className="rounded-2xl overflow-hidden shadow-2xl mb-6">
              <img src="/heroYogaPage.jpg" alt="Yoga" className="w-full h-48 object-cover opacity-90"/>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3 text-white bg-white/10 rounded-xl p-2">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-sm">Access to expert instructors</span>
              </div>
              <div className="flex items-center gap-3 text-white bg-white/10 rounded-xl p-2">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-sm">Flexible class schedules</span>
              </div>
              <div className="flex items-center gap-3 text-white bg-white/10 rounded-xl p-2">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-sm">Personalized wellness plans</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="flex flex-col p-6 sm:p-8 bg-white">
          
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Create Account</h1>
            <p className="text-gray-500">Fill in your details to join us</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <User size={14} className="text-orange-500" />
                Username
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-gray-800 text-sm"
                placeholder="Enter username"
                required
              />
              {errors.username && <p className="text-xs text-red-500 mt-1">{errors.username}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail size={14} className="text-orange-500" />
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-gray-800 text-sm"
                placeholder="Enter email"
                required
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Phone size={14} className="text-orange-500" />
                Mobile
              </label>
              <input
                type="tel"
                name="mobile_no"
                value={formData.mobile_no}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-gray-800 text-sm"
                placeholder="10-digit number"
                required
              />
              {errors.mobile_no && <p className="text-xs text-red-500 mt-1">{errors.mobile_no}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <MapPin size={14} className="text-orange-500" />
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-gray-800 text-sm"
                placeholder="Enter location"
                required
              />
              {errors.location && <p className="text-xs text-red-500 mt-1">{errors.location}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Lock size={14} className="text-orange-500" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-3 py-2 pr-10 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-gray-800 text-sm"
                  placeholder="Min 6 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Lock size={14} className="text-orange-500" />
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-3 py-2 pr-10 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-gray-800 text-sm"
                  placeholder="Re-enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
            </div>

            {errors.submit && <p className="text-sm text-center text-red-500">{errors.submit}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 mt-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus size={20} />
                  Create Account
                </>
              )}
            </button>
          </form>

          <div className="mt-3">
            <button
              onClick={() => setLogin(true)}
              className="w-full py-2.5 border-2 border-gray-200 hover:border-orange-300 text-gray-700 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 hover:bg-orange-50"
            >
              <LogIn size={20} />
              Already have an account? Login
            </button>
          </div>

          <div className="lg:hidden mt-3 pt-3 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">© 2024 Yogamatic. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegister;
